<template>
  <v-container
    id="OP25"
    fluid
    tag="section"
  >
    <div
      align="end"
    >
      <v-btn
        x-large
        elevation="10"
        tile
        color="red"
        to="/"
      >
          Back To Main
      </v-btn>
    </div>
    <base-v-component
      heading="Download Center"
    />
    <base-material-card
      icon="mdi-download"
      title="Download Center"
      class="px-10 py-3"
    >
      <v-data-table
        class="download-center-data-table mt-12 mb-5"
        :headers="table.download.headers"
        :items="table.download.items"
      >
        <template v-slot:[`item.projectType`]="{ item }">
          {{ lodash.get(mapTextProjectType, item.projectType) || '' }}
        </template>
        <template v-slot:[`item.status`]="{ item }">
          <template v-if="canDownloadItem(item)">
            <v-icon color="success" size="24">mdi-check-bold</v-icon>
          </template>
          <template v-else-if="isDownloadingItem(item)">
            <v-progress-circular
              size="24"
              :rotate="-90"
              :color="getDownloadColor(item)"
              :value="getDownloadPercentage(item)"
            ></v-progress-circular>
          </template>
          <template v-else>
            <v-icon color="error lighten-1" size="24">mdi-close-thick</v-icon>
          </template>
          <span class="ml-2">{{ getDownloadItemStatusText(item) }}</span>
        </template>
        <template v-slot:[`item.fileSize`]="{ item }">
          <span v-if="canDownloadItem(item)">
            {{ getItemFileSize(item) }}
          </span>
          <span v-else>-</span>
        </template>
        <template v-slot:[`item.action`]="{ item }">
          <v-btn fab x-small dark class="success ml-2"
            v-if="canDownloadItem(item)"
            @click="clickDownloadItem(item)"
          >
            <v-icon>mdi-download</v-icon>
          </v-btn>
        </template>
      </v-data-table>
    </base-material-card>
    <msal-functions :ref="refMsalFunction"></msal-functions>
  </v-container>
</template>

<script>
import { xPost } from '@/http/axios'
import { mapState } from 'vuex'
import MsalFunctions from '@/components/msal/functions/index.vue'

export default {
  components: {
    MsalFunctions,
  },
  data () { return {
    mapTextProjectType: {
      international: 'International',
      ci: 'CI',
      bases: 'Bases',
    },
    msal: {
      refKey: (new Date).toString().replace(/\W/g, '_'),
    },
    properties: {
      rootId: null,
    },
    table: {
      download: {
        headers: [
          // {
          //   value: 'projectType',
          //   text: 'Project Type',
          // },
          {
            value: 'country',
            text: 'Country',
          },
          {
            value: 'time',
            text: 'Start Time',
            width: '190px',
          },
          {
            value: 'status',
            text: 'Status',
            width: '200px',
            sortable: false,
          },
          {
            value: 'fileSize',
            text: 'File Size',
            width: '160px',
            sortable: false,
          },
          {
            value: '',
            text: '',
            sortable: false,
          },
          {
            value: 'action',
            text: 'Download',
            sortable: false,
          },
        ].map(item => ({
          ...item,
          class: 'black white--text text-body-2 py-4 ' + (item?.class || ''),
        })),
        items: [],
      },
    },
  }},
  computed: {
    // vuex
    ...mapState(['user']),

    // msal
    refMsalFunction () {
      return `msal-function-${this.msal.refKey}`
    },
  },
  methods: {
    async init () {
      const getProperties = this.getProperties()
      await getProperties
      const refreshMyDownloadList = this.refreshMyDownloadList()
      await refreshMyDownloadList
    },

    // actions
    async clickDownloadItem (item) {
      let url = await this.$refs[this.refMsalFunction].getDownloadUrlByRootAndId(this.properties.rootId, item?.fileMsId)
      if (!url) { return }
      let a = document.createElement('a')
      document.body.appendChild(a)
      a.style = 'display:none'
      a.href = url
      // a.target = '_blank'
      a.click()
      window.URL.revokeObjectURL(url)
    },

    // functions
    async getProperties () {
      const { rootId } = await this.postGetProperties()
      this.properties.rootId = rootId?.value
    },
    async refreshMyDownloadList () {
      this.table.download.items = await this.postGetMyDownloadList()
    },
    canDownloadItem (item) {
      return item?.status === '1' && item?.fileMsId
    },
    isDownloadingItem (item) {
      return ['0', '3', '4', '5'].includes(item?.status)
    },
    getDownloadItemStatusText (item) {
      switch (item?.status) {
        case '0':
          return 'Starting Process'

        case '1':
          return 'Completed'
      
        case '2':
          return 'Failed'

        case '3':
          return 'Exporting Data'
      
        case '4':
          return 'Preparing File'

        case '5':
          return 'Downloading File'
      
        default:
          return 'Failed'
      }
    },
    getDownloadColor (item) {
      switch (item?.status) {
        case '0':
        case '3':
        case '4':
          return 'success lighten-1'

        case '5':
          return 'success'
      }},
    getDownloadPercentage (item) {
      switch (item?.status) {
        case '0':
          return 3

        case '3':
          return 25
      
        case '4':
          return 65

        case '5':
          return 80
      }},
    getItemFileSize (item) {
      let pow = 0
      let fileSize = parseInt(item?.fileSize) || 0
      while (typeof fileSize === 'number' && fileSize >= 1024) {
        fileSize = fileSize / 1024.0
        pow++
      }
      let powText = ''
      switch (pow) {
        case 1: powText = 'K';break;
        case 2: powText = 'M';break;
        case 3: powText = 'G';break;
        case 4: powText = 'T';break;
        case 5: powText = 'P';break;
      }
      return (powText ? `${(Math.round(fileSize * 100) / 100)} ${powText}` : item?.fileSize) + 'B'
    },

    // post
    async postGetProperties () {
      let ret = []
      try {
        const res = await xPost(
          'download',
          'props',
          {
            u: this.user?.email
          }
        )
        if (res?.status === 200) {
          ret = res?.data?.props ?? {}
          if (!(ret instanceof Object)) { ret = {} }
        }
      } catch (err) {
        console.log("[Error] download-center postGetProperties")
        // console.log(err)
      }
      return ret
    },
    async postGetMyDownloadList () {
      let ret = []
      try {
        const res = await xPost(
          'download',
          'requests',
          {
            u: this.user?.email
          }
        )
        if (res?.status === 200) {
          ret = res?.data?.downloadRequests ?? []
          if (!(ret instanceof Array)) { ret = [] }
        }
      } catch (err) {
        console.log("[Error] download-center postGetMyDownloadList")
        // console.log(err)
      }
      return ret
    },
  },
  async mounted () {
    await this.init()
  },
}
</script>

<style lang="sass">
  .download-center-data-table tr th i
    color: #c0c0c0 !important
</style>